import React, { useEffect, useState } from 'react';
import {
  Fade,
  Box,
  InputBase,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout from '../../components/MainLayout';
import { ResponsiveHeading } from '../../styles/heading';
import { apps } from '../Application/app';
import { SingletonApplications } from '../ApplicationInsights/appInsights';
import { AppDispatch, RootState } from '../../Redux/store';
import { getConfiguredStatus } from '../../Redux/Slices/Application/applicationHandler';
import { setBreadcrumbOpen } from '../../Redux/Slices/Auditing/auditing';

function Applications() {
  const [searchTerm, setSearchTerm] = useState('');
  const { currentActiveTenantName } = useSelector(
    (state: RootState) => state.customer,
  );
  const { configuredApps, status } = useSelector(
    (state: RootState) => state.application,
  );

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(setBreadcrumbOpen(false));
  }, [dispatch]);

  useEffect(() => {
    if (currentActiveTenantName !== '') {
      dispatch(getConfiguredStatus());
    }
  }, [currentActiveTenantName]);

  const filteredApps = Object.keys(apps).filter((key) => apps[key].title.toLowerCase().includes(searchTerm.toLowerCase()));

  const configuredApplications = filteredApps
    .filter((key) => configuredApps[key.toLowerCase()])
    .map((key) => apps[key]);

  const nonConfiguredApplications = filteredApps
    .filter((key) => !configuredApps[key.toLowerCase()])
    .map((key) => apps[key]);

  const styles = {
    height: { xxl: '15rem', xl: '13rem', lg: '11rem' },
    width: { xxl: '18rem', xl: '14.5rem', lg: '12rem' },
    fontSize: { xxl: '0.875rem', xl: '0.6rem' },
    buttonName: 'Edit',
    headingName: 'Configured Applications',
    imgHeight: { xxl: '2.5rem', xl: '1.8rem', lg: '1.6rem' },
    imgWidth: { xxl: '2.5rem', xl: '1.8rem', lg: '1.6rem' },
    headingFontSize: { xxl: '1.125rem', xl: '1rem', lg: '0.8rem' },
  };

  const nonConfiguredAppsStyles = {
    ...styles,
    headingName: configuredApplications.length === 0 ? 'Applications' : 'Other Applications',
    buttonName: 'Configure',
  };

  return (
    <MainLayout>
      <Fade in timeout={500}>
        {status === 'succeeded' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '98vh',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                zIndex: 1000,
                boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.1)',
                paddingInline: '1rem',
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding="1rem"
              >
                <ResponsiveHeading>Applications</ResponsiveHeading>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    borderRadius: '1.25rem',
                    padding: '0.2rem',
                    backgroundColor: '#F5F5F5',
                    boxShadow: '0rem 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <InputBase
                    placeholder="Search Applications"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ ml: '0.5rem', flex: 1 }}
                  />
                  <IconButton type="submit" sx={{ p: '0.625rem' }}>
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                overflowY: 'auto',
                paddingInline: '1.5rem',
                paddingBottom: '2rem',
                mb: '2rem',
              }}
            >
              <Box>
                <SingletonApplications
                  appName="ConfiguredApps"
                  displayedApps={configuredApplications}
                  styles={styles}
                />
              </Box>

              <Box>
                <SingletonApplications
                  appName="NonConfiguredApps"
                  displayedApps={nonConfiguredApplications}
                  styles={nonConfiguredAppsStyles}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            height="95vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress disableShrink />
          </Box>
        )}
      </Fade>
    </MainLayout>
  );
}

export default Applications;
